import React, { useCallback, useEffect, useMemo, useState } from 'react';
import supabase from '../config/supabase';
import { SUPABASE_URL, SUPABASE_KEY } from '../config/database';
import { invalidatePLCategoryCache } from '../utils/plCategoryResolver';
import InventoryCategoryAssignmentTable from './InventoryCategoryAssignmentTable';
import {
  cloverExclusionKey,
  loadCategoryExclusions,
  saveCategoryExclusions,
} from '../utils/pnlExclusions';
import {
  normalizeName,
  setInventoryMapping,
  setCloverMapping,
} from '../utils/pnlAssignments';

const MUTED = '#8B7B73';
const FONT = "'Montserrat', sans-serif";

/**
 * Mapping-completeness check used to decide whether the EOP report needs a
 * Category Mapping step. A category is "resolved" when it is mapped to a P&L
 * category or explicitly excluded. Returns:
 *   {
 *     pnlCategories, inventoryCategories,
 *     unmappedInventory: [name],
 *     unmappedClover: [{ key, name, type }],
 *     cloverImported, exclusions, required,
 *   }
 */
export async function checkPnlMappingStatus(storeId, { cloverConnected = false, includeClover = true, extraInventoryCategories = [] } = {}) {
  const [pnlRes, invRes, mapRes, exclusions] = await Promise.all([
    supabase.from('pnl_categories').select('id,name,sort_order').eq('store_id', storeId).order('sort_order', { ascending: true }),
    supabase.from('inventory_items').select('category').eq('store_id', storeId).not('category', 'is', null),
    supabase.from('pnl_category_mappings').select('pnl_category_id,inventory_category').eq('store_id', storeId),
    loadCategoryExclusions(storeId),
  ]);

  const pnlCategories = pnlRes.data || [];
  const inventoryCategories = [...new Set([
    ...(invRes.data || []).map((r) => (r.category || '').trim()).filter(Boolean),
    ...extraInventoryCategories.map((c) => (c || '').trim()).filter(Boolean),
  ])].sort();

  const mappedSet = new Set((mapRes.data || []).map((r) => (r.inventory_category || '').toLowerCase()));
  const excludedInvLower = new Set(Object.keys(exclusions.inventory).map((k) => k.toLowerCase()));
  const unmappedInventory = inventoryCategories.filter(
    (c) => !mappedSet.has(c.toLowerCase()) && !excludedInvLower.has(c.toLowerCase()),
  );

  // Clover selling categories are classified into P&L categories later, when
  // sales data is imported and reviewed — so the inventory-category mapping
  // step (and the EOP gate that drives it) can opt out of Clover entirely with
  // includeClover:false and never block on Clover assignments here.
  const useClover = cloverConnected && includeClover;
  let unmappedClover = [];
  let cloverImported = false;
  if (useClover) {
    const { data: cloverRows } = await supabase
      .from('clover_category_mappings')
      .select('clover_category_id, clover_category_name, category_type, pnl_category_id')
      .eq('store_id', storeId);
    cloverImported = (cloverRows || []).length > 0;
    unmappedClover = (cloverRows || [])
      .filter((r) => r.pnl_category_id == null)
      .map((r) => ({
        key: cloverExclusionKey(r.category_type || 'menu_category', r.clover_category_id),
        name: r.clover_category_name,
        type: r.category_type || 'menu_category',
      }))
      .filter((r) => !exclusions.clover[r.key]);
  }

  // Clover connected but its selling categories never imported → the mapping
  // step is required so the live category list can be fetched and assigned.
  const cloverNeedsImport = useClover && !cloverImported;
  const nothingToMap = inventoryCategories.length === 0 && unmappedClover.length === 0 && !cloverNeedsImport;
  const required = !nothingToMap && (
    pnlCategories.length === 0 || unmappedInventory.length > 0 || unmappedClover.length > 0 || cloverNeedsImport
  );

  return { pnlCategories, inventoryCategories, unmappedInventory, unmappedClover, cloverImported, exclusions, required };
}

/**
 * "Clover Categories Included" lookup for the Sales by Category table:
 * pnl_category_id (string) → [clover category names].
 */
export async function loadCloverCategoryNamesByPnl(storeId) {
  const { data } = await supabase
    .from('clover_category_mappings')
    .select('clover_category_name, pnl_category_id')
    .eq('store_id', storeId)
    .not('pnl_category_id', 'is', null);
  const map = {};
  (data || []).forEach((r) => {
    const k = String(r.pnl_category_id);
    if (!map[k]) map[k] = [];
    if (r.clover_category_name && !map[k].includes(r.clover_category_name)) {
      map[k].push(r.clover_category_name);
    }
  });
  return map;
}

// Pull the store's live Clover selling categories (menu categories + revenue
// classes) and their current P&L mappings via the existing edge function.
async function fetchCloverCategories(storeId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated.');
  const resp = await fetch(`${SUPABASE_URL}/functions/v1/clover-selling-categories`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_KEY,
    },
    body: JSON.stringify({ store_id: storeId }),
  });
  const result = await resp.json();
  if (!resp.ok) throw new Error(result.error || 'Failed to load Clover categories.');
  const cats = [
    ...(result.menu_categories || []).map((c) => ({ id: c.id, name: c.name, type: 'menu_category' })),
    ...(result.revenue_classes || []).map((c) => ({ id: c.id, name: c.name, type: 'revenue_class' })),
  ];
  const mapping = {};
  (result.mappings || []).forEach((x) => {
    if (x.pnl_category_id) mapping[`${x.category_type}:${x.clover_category_id}`] = String(x.pnl_category_id);
  });
  return { cats, mapping, revenueClassStatus: result.revenue_class_status || null };
}

/**
 * The permanent category-assignment interface: one row per inventory category
 * (Countertop), each with a searchable P&L reporting-category selector, inline
 * "add P&L category", exclusion, suggested vs confirmed states, and
 * suspicious-mapping review flags. Used by the P&L setup wizard, Settings →
 * P&L Category Mapping, and the EOP report's Category Mapping step, so they
 * share one behavior.
 *
 * Clover selling categories (menu categories + revenue classes) are an optional
 * add-on, shown only when `includeClover` is true AND the store has an active
 * Clover connection. The inventory-category P&L setup step keeps Clover off:
 * mapping sold Clover items into P&L categories happens later, when sales data
 * is imported and reviewed. The EOP report's mapping step opts in so its
 * sales-by-category completeness can still be gated.
 *
 * onStatusChange({ unmappedInventory, unmappedClover }) fires whenever the
 * resolved/unresolved status changes so the wizard can gate its Continue button.
 */
const PNLCategoryMappingPanel = ({
  storeId,
  cloverConnected = false,
  includeClover = true,
  extraInventoryCategories = [],
  onStatusChange,
  onSave,
  onCancel,
  saveLabel = 'Save Category Assignments',
  showIntro = true,
  footerNote = null,
}) => {
  // Clover rows are gated on both the caller opting in and a live connection.
  const showClover = includeClover && cloverConnected;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pnlCats, setPnlCats] = useState([]);
  const [invCats, setInvCats] = useState([]);
  const [invCounts, setInvCounts] = useState({});
  const [invExamples, setInvExamples] = useState({});
  const [invCatToPnlId, setInvCatToPnlId] = useState({});
  const [exclusions, setExclusions] = useState({ inventory: {}, clover: {} });
  const [cloverCats, setCloverCats] = useState([]);
  const [cloverMap, setCloverMap] = useState({});
  const [cloverError, setCloverError] = useState('');
  const [cloverReloading, setCloverReloading] = useState(false);
  // False until the first Clover load settles, so the status callback doesn't
  // briefly report "all resolved" before Clover rows arrive.
  const [cloverReady, setCloverReady] = useState(!showClover);

  const loadClover = useCallback(async () => {
    if (!showClover || !storeId) { setCloverCats([]); setCloverMap({}); setCloverReady(true); return; }
    setCloverReady(false);
    setCloverReloading(true);
    setCloverError('');
    try {
      const { cats, mapping } = await fetchCloverCategories(storeId);
      setCloverCats(cats);
      setCloverMap(mapping);
    } catch (e) {
      setCloverError(e.message || 'Could not load Clover categories.');
    } finally {
      setCloverReloading(false);
      setCloverReady(true);
    }
  }, [storeId, showClover]);

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [pnlRes, invRes, mapRes, ex] = await Promise.all([
          supabase.from('pnl_categories').select('id,name,sort_order').eq('store_id', storeId).order('sort_order', { ascending: true }),
          supabase.from('inventory_items').select('category,description').eq('store_id', storeId).not('category', 'is', null),
          supabase.from('pnl_category_mappings').select('pnl_category_id,inventory_category').eq('store_id', storeId),
          loadCategoryExclusions(storeId),
        ]);
        if (cancelled) return;
        if (pnlRes.error) throw pnlRes.error;
        setPnlCats(pnlRes.data || []);

        const counts = {};
        const examples = {};
        (invRes.data || []).forEach((r) => {
          const c = (r.category || '').trim();
          if (!c) return;
          counts[c] = (counts[c] || 0) + 1;
          const itemName = (r.description || '').trim();
          if (itemName && (examples[c] || []).length < 5 && !(examples[c] || []).includes(itemName)) {
            examples[c] = [...(examples[c] || []), itemName];
          }
        });
        const cats = [...new Set([
          ...Object.keys(counts),
          ...extraInventoryCategories.map((c) => (c || '').trim()).filter(Boolean),
        ])].sort();
        setInvCats(cats);
        setInvCounts(counts);
        setInvExamples(examples);

        const mapping = {};
        (mapRes.data || []).forEach((r) => { mapping[r.inventory_category] = r.pnl_category_id; });
        setInvCatToPnlId(mapping);
        setExclusions(ex);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load category mappings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // extraInventoryCategories is keyed by value so a late-arriving session
  // category list (mapping re-check from the counts step) reloads the panel.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, extraInventoryCategories.join('|')]);

  useEffect(() => { loadClover(); }, [loadClover]);

  // ── Unmapped status (drives the EOP Continue gate) ───────────────────────
  const unmappedInventory = useMemo(
    () => invCats.filter((c) => invCatToPnlId[c] == null && !exclusions.inventory[c]),
    [invCats, invCatToPnlId, exclusions.inventory],
  );
  const unmappedClover = useMemo(
    () => cloverCats
      .filter((c) => {
        const key = cloverExclusionKey(c.type, c.id);
        return cloverMap[key] == null && !exclusions.clover[key];
      })
      .map((c) => ({ key: cloverExclusionKey(c.type, c.id), name: c.name })),
    [cloverCats, cloverMap, exclusions.clover],
  );

  useEffect(() => {
    if (loading || !cloverReady) return;
    if (typeof onStatusChange === 'function') {
      onStatusChange({ unmappedInventory, unmappedClover });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, cloverReady, unmappedInventory, unmappedClover]);

  // ── Build the unified row list for the table ─────────────────────────────
  const rows = useMemo(() => {
    const invRows = invCats.map((name) => ({
      key: `inv:${name}`,
      kind: 'inventory',
      name,
      source: 'Countertop',
      itemCount: invCounts[name] != null ? invCounts[name] : null,
      examples: invExamples[name] || [],
      pnlId: invCatToPnlId[name] != null ? invCatToPnlId[name] : null,
      excluded: !!exclusions.inventory[name],
      exclusionReason: exclusions.inventory[name] || null,
    }));
    const cloverRows = cloverCats.map((c) => {
      const exKey = cloverExclusionKey(c.type, c.id);
      return {
        key: `clover:${c.type}:${c.id}`,
        kind: 'clover',
        name: c.name,
        source: 'Clover',
        cloverId: c.id,
        cloverType: c.type,
        itemCount: null,
        examples: [],
        pnlId: cloverMap[exKey] != null ? Number(cloverMap[exKey]) : null,
        excluded: !!exclusions.clover[exKey],
        exclusionReason: exclusions.clover[exKey] || null,
      };
    });
    return [...invRows, ...cloverRows];
  }, [invCats, invCounts, invExamples, invCatToPnlId, exclusions, cloverCats, cloverMap]);

  const sourceSystems = useMemo(() => {
    const s = [];
    if (invCats.length > 0) s.push('Countertop');
    if (cloverCats.length > 0) s.push('Clover');
    return s;
  }, [invCats, cloverCats]);

  // ── Persistence callbacks ────────────────────────────────────────────────
  const applyInventoryExclusion = async (name, reason) => {
    const inv = { ...exclusions.inventory };
    if (reason) inv[name] = reason; else delete inv[name];
    const next = { ...exclusions, inventory: inv };
    await saveCategoryExclusions(storeId, next);
    setExclusions(next);
  };

  const applyCloverExclusion = async (key, reason) => {
    const clv = { ...exclusions.clover };
    if (reason) clv[key] = reason; else delete clv[key];
    const next = { ...exclusions, clover: clv };
    await saveCategoryExclusions(storeId, next);
    setExclusions(next);
  };

  const handleAssignmentChange = async (row, change) => {
    if (row.kind === 'inventory') {
      if (change.type === 'assign') {
        await setInventoryMapping(supabase, storeId, row.name, change.pnlId);
        setInvCatToPnlId((p) => ({ ...p, [row.name]: change.pnlId }));
        if (exclusions.inventory[row.name]) await applyInventoryExclusion(row.name, null);
      } else if (change.type === 'exclude') {
        await setInventoryMapping(supabase, storeId, row.name, null);
        setInvCatToPnlId((p) => { const n = { ...p }; delete n[row.name]; return n; });
        await applyInventoryExclusion(row.name, change.reason || 'excluded');
      } else {
        await setInventoryMapping(supabase, storeId, row.name, null);
        setInvCatToPnlId((p) => { const n = { ...p }; delete n[row.name]; return n; });
        if (exclusions.inventory[row.name]) await applyInventoryExclusion(row.name, null);
      }
    } else {
      const cat = { id: row.cloverId, name: row.name, type: row.cloverType };
      const exKey = cloverExclusionKey(row.cloverType, row.cloverId);
      if (change.type === 'assign') {
        await setCloverMapping(supabase, storeId, cat, change.pnlId);
        setCloverMap((p) => ({ ...p, [exKey]: String(change.pnlId) }));
        if (exclusions.clover[exKey]) await applyCloverExclusion(exKey, null);
      } else if (change.type === 'exclude') {
        await setCloverMapping(supabase, storeId, cat, null);
        setCloverMap((p) => { const n = { ...p }; delete n[exKey]; return n; });
        await applyCloverExclusion(exKey, change.reason || 'excluded');
      } else {
        await setCloverMapping(supabase, storeId, cat, null);
        setCloverMap((p) => { const n = { ...p }; delete n[exKey]; return n; });
        if (exclusions.clover[exKey]) await applyCloverExclusion(exKey, null);
      }
    }
    invalidatePLCategoryCache(storeId);
  };

  const handleAddPnlCategory = async (name) => {
    const t = name.trim();
    if (!t) throw new Error('Enter a category name.');
    if (pnlCats.some((c) => normalizeName(c.name) === normalizeName(t))) {
      throw new Error(`“${t}” already exists.`);
    }
    const maxOrder = pnlCats.reduce((m, c) => Math.max(m, c.sort_order || 0), -1);
    const { data, error: err } = await supabase
      .from('pnl_categories')
      .insert({ store_id: storeId, name: t, sort_order: maxOrder + 1 })
      .select().single();
    if (err) throw err;
    setPnlCats((prev) => [...prev, data]);
    invalidatePLCategoryCache(storeId);
    return data;
  };

  const handleSave = async ({ confirmAllSuggestions } = {}) => {
    setSaving(true);
    try {
      if (typeof confirmAllSuggestions === 'function') await confirmAllSuggestions();
      if (typeof onSave === 'function') await onSave();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ color: MUTED, fontSize: '0.85rem', padding: '0.75rem 0', fontFamily: FONT }}>Loading category assignments…</div>;
  }

  return (
    <div style={{ fontFamily: FONT }}>
      <InventoryCategoryAssignmentTable
        pnlCats={pnlCats}
        rows={rows}
        sourceSystems={sourceSystems}
        showIntro={showIntro}
        onAssignmentChange={handleAssignmentChange}
        onAddPnlCategory={handleAddPnlCategory}
        onSave={onSave ? handleSave : undefined}
        onCancel={onCancel}
        saveLabel={saveLabel}
        busy={saving}
        error={error}
        footerNote={footerNote}
      />

      {showClover && (
        <div style={{ marginTop: '0.75rem', fontSize: '0.74rem', color: MUTED, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {cloverError
            ? <span style={{ color: '#a05a2c' }}>{cloverError}</span>
            : <span>Clover selling categories are included above.</span>}
          <button type="button" onClick={loadClover} disabled={cloverReloading}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a4a7a', fontSize: '0.74rem', textDecoration: 'underline', padding: 0, fontFamily: FONT }}>
            {cloverReloading ? 'Refreshing…' : '↻ Refresh from Clover'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PNLCategoryMappingPanel;
